import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, RefreshCw, Trash2 } from 'lucide-react';
import {
  SENDING_DOMAIN_STATUS_LABELS,
  type DnsRecord,
  type SendingDomainInput,
  type SendingDomainOverview,
} from '@aimmo/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  toast,
} from '@aimmo/ui';
import { formatDateTime } from '../../lib/format.js';

/** Where the card reads and writes: the agency's own routes, or the operator's for one agency. */
export interface SendingDomainApi {
  key: readonly unknown[];
  get: () => Promise<SendingDomainOverview>;
  set: (input: SendingDomainInput) => Promise<SendingDomainOverview>;
  verify: () => Promise<SendingDomainOverview>;
  remove: () => Promise<SendingDomainOverview>;
}

const STATUS_TONE = {
  pending: 'neutral',
  verified: 'accent',
  failed: 'warning',
} as const;

/**
 * The agency's own sending domain (M10 / P3, decision 4).
 *
 * The one part of email set-up that happens outside the product: the records
 * shown here have to be created, by hand, wherever the agency's DNS lives.
 * So the card leads with what mail is sent from *right now* — the platform's
 * address until SES has seen the records — and puts every record a click away
 * from the clipboard.
 */
/** What a mutation of the domain does once the API has answered: the answer is the new state. */
function useSettle(api: SendingDomainApi) {
  const queryClient = useQueryClient();
  return (message: string) => ({
    onSuccess: (data: SendingDomainOverview) => {
      queryClient.setQueryData(api.key, data);
      toast.success(message);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * The domain and the sender, as typed. Keyed by what is registered, so it
 * starts from the registration and starts again when that changes — never
 * when the status is merely re-read, which would overwrite a half-typed name.
 */
function DomainForm({
  api,
  registered,
}: {
  api: SendingDomainApi;
  registered: SendingDomainOverview['domain'];
}) {
  const settle = useSettle(api);
  const [domain, setDomain] = React.useState(registered?.domain ?? '');
  const [fromLocalPart, setFromLocalPart] = React.useState(
    registered?.fromLocalPart ?? 'newsletter',
  );

  const save = useMutation({
    mutationFn: () => api.set({ domain, fromLocalPart }),
    ...settle('Dominio registrato: crea i record DNS indicati.'),
  });

  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_12rem_auto] sm:items-end">
      <div className="grid gap-1">
        <Label htmlFor="sending-domain">Dominio</Label>
        <Input
          id="sending-domain"
          placeholder="agenzia.it"
          value={domain}
          onChange={(event) => setDomain(event.target.value)}
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="sending-local-part">Mittente</Label>
        <Input
          id="sending-local-part"
          value={fromLocalPart}
          onChange={(event) => setFromLocalPart(event.target.value)}
        />
      </div>
      <Button
        type="button"
        variant="secondary"
        disabled={!domain.trim() || save.isPending}
        onClick={() => save.mutate()}
      >
        Registra dominio
      </Button>
    </div>
  );
}

export function SendingDomainCard({ api, canEdit }: { api: SendingDomainApi; canEdit: boolean }) {
  const status = useQuery({ queryKey: api.key, queryFn: api.get });
  const settle = useSettle(api);

  const verify = useMutation({ mutationFn: api.verify, ...settle('Verifica aggiornata.') });
  const remove = useMutation({
    mutationFn: api.remove,
    ...settle('Dominio rimosso: le email partono dal dominio della piattaforma.'),
  });

  if (status.isPending) return <Skeleton className="h-40 w-full" />;
  if (!status.data) return null;
  const data = status.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dominio di invio email</CardTitle>
        <CardDescription>
          Le email partono ora da <strong>{data.senderInEffect}</strong>
          {data.domain?.status === 'verified'
            ? '.'
            : ', con le risposte all’email dell’agenzia. Con un dominio verificato partono dal vostro.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {canEdit && (
          <DomainForm
            key={data.domain ? `${data.domain.fromLocalPart}@${data.domain.domain}` : 'none'}
            api={api}
            registered={data.domain}
          />
        )}

        {data.domain && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[data.domain.status]}>
                {SENDING_DOMAIN_STATUS_LABELS[data.domain.status]}
              </Badge>
              <span className="text-secondary text-muted">
                {data.domain.fromLocalPart}@{data.domain.domain}
                {data.domain.lastCheckedAt &&
                  ` · controllato il ${formatDateTime(data.domain.lastCheckedAt)}`}
              </span>
              {canEdit && (
                <span className="ml-auto flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={verify.isPending}
                    onClick={() => verify.mutate()}
                  >
                    <RefreshCw /> Verifica ora
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    <Trash2 /> Rimuovi
                  </Button>
                </span>
              )}
            </div>

            {data.domain.status !== 'verified' && (
              <p className="text-secondary text-muted">
                Crea questi record nel DNS del dominio. La verifica può richiedere qualche ora dopo
                la loro pubblicazione.
              </p>
            )}
            <DnsRecords records={[...data.domain.dkimRecords]} />
            <div className="grid gap-1">
              <p className="text-secondary text-muted">
                Consigliato, se il dominio non ha già una politica DMARC:
              </p>
              <DnsRecords records={[data.domain.dmarcRecord]} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DnsRecords({ records }: { records: DnsRecord[] }) {
  const copy = (value: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => toast.success('Copiato.'))
      .catch(() => toast.error('Copia non riuscita.'));
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-secondary">
        <thead>
          <tr className="text-faint">
            <th className="py-1 pr-3 font-normal">Tipo</th>
            <th className="py-1 pr-3 font-normal">Nome</th>
            <th className="py-1 font-normal">Valore</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr key={record.name} className="border-t border-border align-top">
              <td className="py-1 pr-3">{record.type}</td>
              {[record.name, record.value].map((value) => (
                <td key={value} className="py-1 pr-3">
                  <span className="flex items-center gap-1">
                    <code className="break-all text-ink">{value}</code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Copia ${value}`}
                      onClick={() => copy(value)}
                    >
                      <Copy />
                    </Button>
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
